import { sanitizeObservabilityMetadata } from "utils/privacy-safe-observability";

type UploadMetadataValue = string | number | boolean | null | undefined;
type SanitizedUploadMetadata = Record<string, string | number | boolean>;

export type UploadObservabilityMetadata = Record<string, UploadMetadataValue>;

function sanitizeMetadata(metadata?: UploadObservabilityMetadata) {
  return sanitizeObservabilityMetadata(metadata) as SanitizedUploadMetadata;
}

function formatConsoleMetadata(metadata?: UploadObservabilityMetadata) {
  const sanitizedMetadata = sanitizeMetadata(metadata);

  return Object.keys(sanitizedMetadata).length > 0
    ? sanitizedMetadata
    : undefined;
}

export function logUploadBreadcrumb(
  message: string,
  metadata?: UploadObservabilityMetadata
) {
  // Lazy-load @opentelemetry/api to keep it out of the initial bundle.
  // The trace API is a no-op when no provider is registered, so the
  // dynamic import delay is harmless for breadcrumbs.
  void import("@opentelemetry/api")
    .then(({ trace }) => {
      trace.getActiveSpan()?.addEvent(message, sanitizeMetadata(metadata));
    })
    .catch((error) => {
      console.warn(
        "[upload-observability] failed to add upload breadcrumb (non-fatal)",
        error
      );
    });
}

export function logUploadFailure(
  message: string,
  error: unknown,
  metadata?: UploadObservabilityMetadata
) {
  const errorMetadata =
    error instanceof Error
      ? {
          errorName: error.name,
          errorMessage: error.message,
        }
      : {
          errorMessage: String(error),
        };

  const combinedMetadata = {
    ...metadata,
    ...errorMetadata,
  };

  logUploadBreadcrumb(message, combinedMetadata);
  console.error(
    `[media_upload] ${message}`,
    formatConsoleMetadata(combinedMetadata)
  );
}

export async function withUploadSpan<T>(
  name: string,
  metadata: UploadObservabilityMetadata | undefined,
  callback: () => Promise<T>
) {
  const [{ withObservabilitySpan }, { SpanStatusCode }] = await Promise.all([
    import("utils/observability"),
    import("@opentelemetry/api"),
  ]);

  return withObservabilitySpan(
    name,
    sanitizeMetadata(metadata),
    async (span) => {
      const result = await callback();
      span?.setStatus({ code: SpanStatusCode.OK });
      return result;
    }
  );
}
